import {
	Card,
	CardActionArea,
	CardContent,
	Stack,
	Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import { useAuthContext } from '../../context/auth';
import { useClaimContext } from '../../context/claim';
import { useConfigContext } from '../../context/config';
import { useErrorContext } from '../../context/ErrorProvider';
import { usePolicyContext } from '../../context/policy';
import {
	CLAIM_SAVE_BUTTON_TEXT,
	CLAIM_SAVE_MODAL_NO_COVERAGE_WARNING_MSG,
	CLAIM_SAVE_MODAL_NO_COVERAGE_WARNING_TITLE,
	CLAIM_SAVE_MODAL_NO_SUBCLAIM_COVERAGE_WARNING_TITLE,
	CLAIM_SAVE_MODAL_SUBCLAIM_WARNING_MSG,
	CLAIM_SAVE_MODAL_WARNING_CANCEL_BUTTON,
	CLAIM_SAVE_MODAL_WARNING_CONFIRM_BUTTON,
	CLAIM_SAVE_MODAL_WARNING_EXPENSE_RESERVE,
	CLAIM_SAVE_MODAL_WARNING_LOSS_RESERVE,
	CLAIM_SAVE_MODAL_WARNING_SUBCLAIM,
	CLAIM_UPDATED_FAILURE_TOAST_MSG,
	CLAIM_UPDATED_SUCCESS_TOAST_MSG,
} from '../../data/labelsAndMessages';
import DefaultDialog from '../DefaultDialog';
import { DATE_PRECISION } from '../fastForm/data/constants';
import { dateConversion } from '../fastForm/utils';
import { successToast } from '../Toast';
import { checkForInvalidatedSubclaims } from './util';

const UpdateOpenClaimModal = () => {
	const authData = useAuthContext();
	const claimData = useClaimContext();
	const policyData = usePolicyContext();
	const errorData = useErrorContext();
	const configData = useConfigContext();
	const { getValues, formState, reset, control } = useFormContext();
	const { dirtyFields } = formState;
	const {
		claimUpdateReq,
		claim,
		setClaimUpdateReq,
		setEditMode,
		saveFastForm,
	} = claimData;
	const timezone = authData.auth.timezone;
	const { policy } = policyData;
	const [isOpen, setIsOpen] = React.useState(false);
	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);

	const formIncidentDateString = useWatch({
		control,
		name: 'claim.incidentDate',
	});

	const incidentTimestamp =
		dateConversion.convertDateStringToZonedTimestamp(
			formIncidentDateString,
			DATE_PRECISION.DAY,
			timezone,
		) || claim.incidentTimestamp;
	const invalidSubclaims = checkForInvalidatedSubclaims(
		incidentTimestamp,
		claim?.subClaims,
		policy,
		configData,
	);
	const { originalContractStartTimestamp, effectiveContractEndTimestamp } =
		policy;

	const originalContractStartDate = dateConversion.convertTimestampToZonedDayJS(
		originalContractStartTimestamp,
		timezone,
	);
	const effectiveContractEndDate = dateConversion.convertTimestampToZonedDayJS(
		effectiveContractEndTimestamp,
		timezone,
	);
	const incidentDate = dateConversion.convertTimestampToZonedDayJS(
		incidentTimestamp,
		timezone,
	);
	const noCoverageInForce =
		incidentDate.tz() < originalContractStartDate.tz() ||
		incidentDate.tz() > effectiveContractEndDate.tz();
	const shouldOpenModal = invalidSubclaims?.length || noCoverageInForce;

	const updateClaim = async () => {
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				openClaim: true,
			});
			setClaimUpdateReq({});
			setEditMode(false);
			successToast(CLAIM_UPDATED_SUCCESS_TOAST_MSG());
		} catch (error) {
			errorData.handleErrorResponse(error, CLAIM_UPDATED_FAILURE_TOAST_MSG());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			// if update failed, remove status of 'open'
			delete claimUpdateReq.status;
		}
		// Do not perform a state update on a non-mounted component
		shouldOpenModal && close();
	};

	const openModal = () => {
		if (shouldOpenModal) {
			open();
		} else {
			updateClaim();
		}
	};

	const renderInvalidatedSubclaimCards = () => {
		return invalidSubclaims.map((subclaim) => (
			<Card sx={{ p: 0, mt: '10px' }}>
				<CardActionArea
					component={Link}
					to={`../subclaims/${subclaim.locator}`}
				>
					<CardContent sx={{ p: '12px' }}>
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<Typography fontWeight='fontWeightMedium' variant='subtitle1'>
								{CLAIM_SAVE_MODAL_WARNING_SUBCLAIM()} {subclaim.locator} -{' '}
								{subclaim.perilDisplayName}
							</Typography>
							<Typography fontWeight='fontWeightMedium' variant='subtitle1'>
								{subclaim.subclaimStatus}
							</Typography>
						</div>
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<Typography>
								{CLAIM_SAVE_MODAL_WARNING_LOSS_RESERVE()}{' '}
								{subclaim.totalLossReserve}
							</Typography>
							<Typography>
								{CLAIM_SAVE_MODAL_WARNING_EXPENSE_RESERVE()}
								{subclaim.totalExpenseReserve}
							</Typography>
						</div>
					</CardContent>
				</CardActionArea>
			</Card>
		));
	};

	const renderContent = () => {
		return (
			<Stack direction='column' spacing={1}>
				{noCoverageInForce ? (
					<div>{CLAIM_SAVE_MODAL_NO_COVERAGE_WARNING_MSG()}</div>
				) : null}
				{invalidSubclaims?.length ? (
					<div>
						{CLAIM_SAVE_MODAL_SUBCLAIM_WARNING_MSG()}
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							{renderInvalidatedSubclaimCards()}
						</div>
					</div>
				) : null}
			</Stack>
		);
	};

	const modalTitle = noCoverageInForce
		? CLAIM_SAVE_MODAL_NO_COVERAGE_WARNING_TITLE()
		: CLAIM_SAVE_MODAL_NO_SUBCLAIM_COVERAGE_WARNING_TITLE();

	return (
		<>
			<Button
				color='primary'
				onClick={openModal}
				variant='contained'
				disabled={!formState.isDirty}
			>
				{CLAIM_SAVE_BUTTON_TEXT()}
			</Button>
			<DefaultDialog
				actions={[
					{
						text: CLAIM_SAVE_MODAL_WARNING_CONFIRM_BUTTON(),
						onClick: updateClaim,
					},
					{ text: CLAIM_SAVE_MODAL_WARNING_CANCEL_BUTTON(), onClick: close },
				]}
				open={isOpen}
				onClose={close}
				title={modalTitle}
			>
				{renderContent()}
			</DefaultDialog>
		</>
	);
};

export default UpdateOpenClaimModal;
